import React, { ReactNode, useCallback } from 'react'
import { FastField, Formik, FieldProps } from 'formik'
import { isEmpty } from 'ramda'
import * as yup from 'yup'
import { FormField, FormLoader, Modal, TextField, TextInput } from '../../../../../views/components'
import { splitCamelCase } from 'utils/string'
import styles from './ResendEmailModal.module.scss'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'

interface SelectedEmail {
  email?: string
  messageId?: string
  type?: string
}

interface FormValues {
  emailAddress?: string
}

interface ResendEmailModalProps {
  isModalOpen: boolean
  onClose: () => void
  selectedEmail: SelectedEmail
  onSubmit: (messageId: string | undefined, recipients: string[]) => void
}

const validationSchema = yup.object().shape({
  emailAddress: yup.string().email('Email address format is invalid.'),
})

const ButtonContainer = ({ children }: { children: ReactNode }) => <div className="modal-action">{children}</div>

const ResendEmailModal = ({ isModalOpen, onClose, selectedEmail, onSubmit }: ResendEmailModalProps) => {
  const { messageId, email, type } = selectedEmail

  const handleModalClose = (): void => {
    onClose()
  }

  const handleSubmit = useCallback(
    (values: FormValues) => {
      onSubmit(messageId, [values.emailAddress ?? ''])
    },
    [onSubmit, messageId],
  )

  return (
    <Modal isOpen={isModalOpen} onClose={() => handleModalClose()} width="md">
      <h3 className={styles['modal-header']}>Resend Email</h3>
      <div className={styles['modal-info']}>
        <span className={styles['modal-item-wrapper']}>
          <div className={styles['modal-label']}>Email type:</div>
          {splitCamelCase(type)}
        </span>
        <span className={styles['modal-item-wrapper']}>
          <div className={styles['modal-label']}>Original recipient:</div>
          {email}
        </span>
      </div>

      <div className="modal-content">
        <Formik
          initialValues={{ emailAddress: selectedEmail?.email }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ setFieldValue, handleSubmit, isSubmitting, values, errors }) => {
            const formLoaderState = isSubmitting ? 'submitting' : !isEmpty(errors) ? 'error' : undefined

            return (
              <FormLoader
                onSubmit={handleSubmit}
                state={formLoaderState}
                buttonProps={{ children: 'Send' }}
                ButtonContainer={ButtonContainer}
              >
                <FormField>
                  <FastField
                    name="emailAddress"
                    render={({ field }: FieldProps) => (
                      <TextField
                        {...field}
                        inputComponent={
                          <TextInput
                            name={field.name}
                            type={TextFieldTypes.text}
                            value={values.emailAddress}
                            onChange={e => setFieldValue('emailAddress', e.target.value)}
                          />
                        }
                        error={errors?.emailAddress}
                        label="Recipient"
                      />
                    )}
                  />
                </FormField>
              </FormLoader>
            )
          }}
        </Formik>
      </div>
    </Modal>
  )
}

export default ResendEmailModal
